import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import { IntroDialog } from './IntroDialog';
import { useMusic } from '../hooks/useMusic';

/**
 * WorldMap Component - Final Version
 * Using explicit inline styles to ensure layout stability across all environments.
 */
export const WorldMap: React.FC = () => {
    const t = useTranslation();
    const mapRef = useRef<HTMLImageElement>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapHeight, setMapHeight] = useState(0);
    const navigate = useNavigate();
    const { hasSeenIntro, setHasSeenIntro, musicEnabled, musicVolume } = useGameStore();

    // Фоновая музыка для карты мира
    useMusic({ type: 'menu', volume: musicVolume, enabled: musicEnabled });

    const handleIntroEnd = () => {
        setHasSeenIntro(true);
        // Не переходим автоматически в базу - пользователь должен сам выбрать базу на карте
        // navigate('/base');
    };

    const HEADER_HEIGHT = 100;

    // Calculate map dimensions
    const calculateLayout = () => {
        if (mapRef.current) {
            const cw = window.innerWidth;
            const nw = mapRef.current.naturalWidth;
            const nh = mapRef.current.naturalHeight;

            // Map fills screen width
            const targetHeight = (cw / nw) * nh;
            setMapHeight(targetHeight);
            setMapLoaded(true);
        }
    };

    useEffect(() => {
        // Initial call + listeners
        calculateLayout();
        window.addEventListener('resize', calculateLayout);

        // Safety timeout for mobile browsers
        const t1 = setTimeout(calculateLayout, 300);
        const t2 = setTimeout(calculateLayout, 1000);

        return () => {
            window.removeEventListener('resize', calculateLayout);
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    // Автоматическая прокрутка к базе при загрузке карты
    useEffect(() => {
        if (mapLoaded && mapContainerRef.current && mapHeight > 0) {
            const container = mapContainerRef.current;
            const containerHeight = container.clientHeight;

            // База находится на bottom: 20%, то есть 80% от верха
            const baseY = mapHeight * 0.8;

            // Центрируем базу: позиция базы - половина высоты экрана
            const targetScrollTop = baseY - (containerHeight / 2);

            // Небольшая задержка для уверенности, что рендеринг завершен
            setTimeout(() => {
                container.scrollTo({
                    top: Math.max(0, targetScrollTop),
                    behavior: 'smooth'
                });
            }, 100);
        }
    }, [mapLoaded, mapHeight]);

    // Предотвращаем автоматический переход в базу во время туториала
    // (эта проверка не нужна, так как автоматический переход происходит только через IntroDialog)

    // Styles
    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0)',
        bottom: 'env(safe-area-inset-bottom, 0)',
        left: 'env(safe-area-inset-left, 0)',
        right: 'env(safe-area-inset-right, 0)',
        backgroundColor: '#000',
        overflow: 'hidden',
        zIndex: 9999,
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        display: 'flex',
        flexDirection: 'column'
    };

    const headerLayerStyle: React.CSSProperties = {
        height: `${HEADER_HEIGHT}px`,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0.8))',
        zIndex: 100,
        borderBottom: '2px solid #ca8a04',
        flexShrink: 0
    };

    const mapContainerStyle: React.CSSProperties = {
        position: 'relative',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        zIndex: 10
    };

    const mapLayerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        minHeight: mapHeight > 0 ? mapHeight : '100%',
        paddingBottom: '200px', // Space for footer and base icon (increased for better visibility)
        zIndex: 10
    };

    const footerLayerStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        padding: '2rem 0',
        textAlign: 'center',
        background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
        zIndex: 100,
        pointerEvents: 'none',
        flexShrink: 0
    };

    const titleStyle: React.CSSProperties = {
        color: '#fbbf24', // yellow-400/500
        fontSize: '1.5rem',
        fontWeight: '900',
        margin: 0,
        letterSpacing: '0.1em',
        textShadow: '0 0 20px rgba(251, 191, 36, 0.4)',
        fontFamily: '"Press Start 2P", cursive, sans-serif'
    };

    const menuButtonStyle: React.CSSProperties = {
        backgroundColor: '#ca8a04', // yellow-600
        color: '#000',
        padding: '0.75rem 1.5rem',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        border: 'none',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        boxShadow: '0 4px 0 #854d0e',
        display: 'inline-block',
        transition: 'none',
        pointerEvents: 'auto'
    };

    return (
        <>
            <style>{`
                /* Custom scrollbar for map container */
                .world-map-scroll::-webkit-scrollbar {
                    width: 12px;
                }
                .world-map-scroll::-webkit-scrollbar-track {
                    background: rgba(0, 0, 0, 0.3);
                }
                .world-map-scroll::-webkit-scrollbar-thumb {
                    background: rgba(202, 138, 4, 0.6);
                    border-radius: 6px;
                    border: 2px solid rgba(0, 0, 0, 0.3);
                }
                .world-map-scroll::-webkit-scrollbar-thumb:hover {
                    background: rgba(251, 191, 36, 0.8);
                }
                /* Firefox */
                .world-map-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(202, 138, 4, 0.6) rgba(0, 0, 0, 0.3);
                }
                
                /* Base icon glow animation */
                @keyframes baseGlow {
                    0%, 100% {
                        filter: drop-shadow(0 0 10px #fbbf24) drop-shadow(0 0 20px rgba(251, 191, 36, 0.6));
                        opacity: 1;
                    }
                    50% {
                        filter: drop-shadow(0 0 20px #fbbf24) drop-shadow(0 0 30px rgba(251, 191, 36, 0.9)) drop-shadow(0 0 40px rgba(251, 191, 36, 0.5));
                        opacity: 0.95;
                    }
                }
                
                .base-icon-glow {
                    animation: baseGlow 2s ease-in-out infinite;
                }
                
                .base-icon-glow:hover {
                    animation: none;
                    filter: drop-shadow(0 0 25px #fbbf24) drop-shadow(0 0 35px rgba(251, 191, 36, 1)) drop-shadow(0 0 45px rgba(251, 191, 36, 0.7));
                }
            `}</style>
            <div style={containerStyle}>
                {/* UI Header */}
                <div style={headerLayerStyle}>
                    <h1 style={titleStyle}>Heroes of Last Citadel Tactics</h1>
                </div>

                {/* Map Container with Scroll */}
                <div
                    ref={mapContainerRef}
                    style={mapContainerStyle}
                    className="world-map-scroll"
                >
                    <div style={mapLayerStyle}>
                        <div style={{ position: 'relative', width: '100%', display: 'inline-block' }}>
                            <img
                                ref={mapRef}
                                src="./sprites/world/world_map.png"
                                alt="World Map"
                                onLoad={calculateLayout}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />

                            {/* Human Base Icon - positioned relative to map image */}
                            {mapLoaded && (
                                <button
                                    onClick={() => navigate('/base')}
                                    className="base-icon-glow"
                                    style={{
                                        position: 'absolute',
                                        bottom: '20%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 'clamp(3rem, 6vw, 8rem)',
                                        height: 'clamp(3rem, 6vw, 8rem)',
                                        minWidth: '48px',
                                        minHeight: '48px',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        zIndex: 50,
                                        padding: 0,
                                        transition: 'transform 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateX(-50%) scale(1.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                                    }}
                                >
                                    <img
                                        src="./sprites/bases/base_humans.png"
                                        alt="Citadel"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* UI Footer */}
                <div style={footerLayerStyle}>
                    <p style={{
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.5rem',
                        marginBottom: '1rem',
                        letterSpacing: '0.05em',
                        textShadow: '1px 1px 2px #000'
                    }}>
                        {t.game.clickBaseHint}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        style={menuButtonStyle}
                        onMouseDown={(e) => {
                            e.currentTarget.style.transform = 'translateY(2px)';
                            e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
                        }}
                        onMouseUp={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
                        }}
                    >
                        {t.common.backToMenu}
                    </button>
                </div>

                {/* Initial Loading Screen */}
                {!mapLoaded && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#000',
                        zIndex: 10000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ width: '2rem', height: '2rem', border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#fbbf24', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <span style={{ marginTop: '1rem', color: '#fbbf24', fontSize: '0.75rem', letterSpacing: '0.2em' }}>{t.game.loadingWorld}</span>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                )}

                {/* Introductory Dialogue */}
                {!hasSeenIntro && <IntroDialog onEnd={handleIntroEnd} />}
            </div>
        </>
    );
};
